// PopoutWindow — render React children into a REAL separate browser window
// (window.open), not an in-page overlay. Used by the JAWS workbench so the
// kinematics charts and any feature panel can be "popped out" into their own OS
// window. Copies the app's stylesheets into the new window so Tailwind classes
// render, and calls onClose when the user closes the window (so the caller can
// revert to the in-page view — the pop-out is never permanent).
//
// Popup-blocked handling (browsers block window.open outside a user gesture, or
// when the user has blocked popups for the site). window.open returns null when
// blocked, which we detect. Two fallbacks, chosen by `fallback`:
//   • 'inViewer' (charts) → render the content in TK's OWN in-viewer floating
//     window so the graphs still show, with a hint to unblock popups.
//   • 'close' (feature panels, default) → revert to the in-page view (onClose),
//     since those panels already have an in-window form right there.

import { useEffect, useState, type ReactNode } from 'react';
import { createPortal } from 'react-dom';

export function PopoutWindow({
  title,
  width = 900,
  height = 640,
  onClose,
  fallback = 'close',
  children,
}: {
  title: string;
  width?: number;
  height?: number;
  onClose: () => void;
  /** What to do if the browser BLOCKS the pop-out window. */
  fallback?: 'inViewer' | 'close';
  children: ReactNode;
}) {
  const [container, setContainer] = useState<HTMLElement | null>(null);
  // The browser blocked window.open — show the in-viewer fallback instead.
  const [blocked, setBlocked] = useState(false);

  useEffect(() => {
    const win = window.open(
      '',
      '',
      `width=${width},height=${height},menubar=no,toolbar=no,location=no,status=no`,
    );
    // Popup blocked (no user gesture / blocker). For 'inViewer' content we show
    // TK's own in-viewer window (below); for 'close' we revert to the in-page
    // view. We can't programmatically prompt the browser to allow the popup —
    // there's no such web API — so detecting the block IS the mechanism.
    if (!win) {
      if (fallback === 'inViewer') setBlocked(true);
      else onClose();
      return;
    }
    win.document.title = title;
    // Stylesheets: re-create <link>s with their ABSOLUTE href (a relative href
    // would resolve against about:blank and 404), and clone inline <style>s
    // (Vite dev injects CSS this way).
    document
      .querySelectorAll<HTMLLinkElement>('link[rel="stylesheet"]')
      .forEach((n) => {
        const link = win.document.createElement('link');
        link.rel = 'stylesheet';
        link.href = n.href;
        win.document.head.appendChild(link);
      });
    document.querySelectorAll('style').forEach((n) => {
      win.document.head.appendChild(n.cloneNode(true));
    });
    win.document.body.style.margin = '0';
    win.document.body.style.background = '#121516';
    const mount = win.document.createElement('div');
    win.document.body.appendChild(mount);
    setContainer(mount);

    const handleWinClose = () => onClose();
    win.addEventListener('beforeunload', handleWinClose);
    // If the parent tab unloads, take the child window with it.
    const closeChild = () => win.close();
    window.addEventListener('beforeunload', closeChild);

    return () => {
      win.removeEventListener('beforeunload', handleWinClose);
      window.removeEventListener('beforeunload', closeChild);
      win.close();
    };
    // Open exactly once for this pop-out; children re-portal into `mount` on
    // change. title/size are read once at open — intentional.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Tier-2 fallback: the OS pop-out was blocked, so show TK's own in-viewer
  // floating window with the same content, plus a hint to unblock popups.
  if (blocked) {
    return createPortal(
      <div className="pointer-events-none fixed inset-0 z-[100] flex items-center justify-center p-4">
        <div
          className="pointer-events-auto flex max-h-full flex-col overflow-hidden rounded-lg border border-telekinesis-neutral-700 bg-telekinesis-bg-secondary-dark shadow-2xl"
          style={{ width, height, maxWidth: '95vw', maxHeight: '90vh' }}
        >
          <div className="flex items-center justify-between gap-3 border-b border-telekinesis-neutral-700 px-3 py-1.5">
            <span className="text-xs font-medium text-telekinesis-neutral-50">
              {title}
            </span>
            <div className="flex items-center gap-2">
              <span className="hidden text-[10px] text-telekinesis-text-secondary sm:inline">
                Popups blocked — unblock to open in a window
              </span>
              <button
                type="button"
                onClick={onClose}
                className="rounded px-1.5 text-sm text-telekinesis-text-secondary hover:text-telekinesis-neutral-50"
                title="Close"
              >
                ✕
              </button>
            </div>
          </div>
          <div className="min-h-0 flex-1 overflow-auto">{children}</div>
        </div>
      </div>,
      document.body,
    );
  }

  return container ? createPortal(children, container) : null;
}
